import { Checkbox, Modal, Result, Typography } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { OrangeButton, ProgressBar } from 'components/ui'
import { productApi } from 'api/product'
import { useSocketRequest } from 'shared/hooks/useSocketRequest'
import styles from './AiMassModal.module.scss'

interface AiMassModalProps {
  visible: boolean
  productIds: string[]
  onSuccess: () => void
  onClose: () => void
}

const isValid = (str: string) => /^\d+\/\d+$/.test(str)

export const AiMassModal: React.FC<AiMassModalProps> = ({
  visible,
  productIds,
  onSuccess,
  onClose
}) => {
  const [description, setDescription] = useState(false)
  const [characteristics, setCharacteristics] = useState(false)
  const [image, setImage] = useState(false)

  const [step, setStep] = useState(1)

  const createJob = useCallback(async () => {
    const { data } = await productApi.massAi({
      productIds,
      description,
      characteristics,
      image
    })
    return { jobId: data.jobId }
  }, [productIds, description, characteristics, image])

  const { isLoaded, isError, progressData, retry } = useSocketRequest({
    enabled: false,
    createJob
  })

  const closeModal = () => {
    setStep(1)
    onClose()
  }

  const handleStartMassAi = () => {
    retry()
    setStep(2)
  }

  useEffect(() => {
    if (progressData?.percent === 100) {
      onSuccess()
    }
  }, [progressData])

  const progressValue = useMemo(() => {
    if (progressData?.message && isValid(progressData.message)) {
      return progressData.message
    }
    return `0/${productIds.length}`
  }, [progressData, productIds])

  const progressPercent = useMemo(() => {
    return progressData?.percent || 0
  }, [progressData])

  return (
    <Modal
      title='Массовое AI'
      open={visible}
      onCancel={closeModal}
      footer={null}
      closeIcon={<CloseOutlined />}
      className={styles.modalContainer}
      centered
    >
      {step === 1 && (
        <>
          <div className={styles.contentWrapper}>
            <Checkbox
              checked={description}
              onChange={(e) => setDescription(e.target.checked)}
            >
              Reach описание
            </Checkbox>
            <Checkbox
              checked={characteristics}
              onChange={(e) => setCharacteristics(e.target.checked)}
            >
              Характеристики
            </Checkbox>
            <Checkbox
              checked={image}
              onChange={(e) => setImage(e.target.checked)}
            >
              Картинка товара
            </Checkbox>
          </div>
          <Typography.Text className={styles.text}>Внимание, если вы выбираете AI-улучшение, мы не несем никакой ответственности за итоговый результат.</Typography.Text>
          <OrangeButton size='large' className={styles.button} onClick={handleStartMassAi}><span className='aiButtonIcon'>􀜍</span> Начать массовое AI</OrangeButton>
        </>
      )}
      {step === 2 && (
        <div className={styles.progressWrapper}>
          {!isLoaded && progressPercent !== 100 && (
            <>
              <Typography.Text className={styles.progressValue}>{progressValue}</Typography.Text>
              <ProgressBar percent={progressPercent} />
              <Typography.Text className={styles.progressText}>Идет генерация запроса, ожидайте...</Typography.Text>
            </>
          )}
          {isError && isLoaded && (
            <Result
              status='error'
              title='Ошибка при генерации'
              subTitle='Попробуйте еще раз'
              extra={[
                <OrangeButton onClick={retry}>
                  Попробовать еще раз
                </OrangeButton>
              ]}
            />
          )}
          {progressPercent === 100 && (
            <Result
              status='success'
              title='Генерация успешно завершена'
              subTitle='Вы можете закрыть это окно'
            />
          )}
        </div>
      )}
    </Modal>
  )
}